import {
  facebookIcon,
  instagramIcon,
  linkedinIcon,
  twitterIcon,
} from '@jg/icons';
import Svg from '@jg/svg';
import classNames from 'classnames';
import Preact from 'preact';
import bbP2PLogo from '../../components/Logo/bbP2PLogo.svg';
import jgLogo from '../../components/Logo/jgLogo.svg';
import jgLogoNobel from '../../components/Logo/jgLogoNobel.svg';
import jgLogoNew from '../../components/Logo/jgLogoNew.svg';
import { FooterContent } from '../../content';
import testId from '../../helpers/testId';

const socialIcons: Record<string, string> = {
  facebook: facebookIcon,
  twitter: twitterIcon,
  instagram: instagramIcon,
  linkedIn: linkedinIcon,
};

type Props = Pick<
  FooterContent,
  'logo' | 'secondary' | 'copyright' | 'fcaTagline' | 'social' | 'findUs'
>;

const logoSrc = (version: FooterContent['logo']['version']) => {
  switch (version) {
    case 'jgGrey':
      return jgLogoNobel;

    case 'jgDefault':
      return jgLogo;

    case 'jgNewLogo':
      return jgLogoNew;

    case 'bbP2P':
      return bbP2PLogo;
  }
};

const FooterBlock: Preact.FunctionalComponent<Props> = ({
  logo,
  secondary,
  copyright,
  fcaTagline,
  social,
  findUs,
}) => (
  <div className="container">
    <div className="row jg-space-phxs@md jg-space-pbsm@xl">
      <div
        className={classNames(
          social && social.length > 0 ? 'col-xs-12 col-xl-9' : 'col-xs-12',
          {
            // If there are social buttons but no copyright, balance the columns
            'jg-space-ptsm@xl': Boolean(social && !copyright),
          },
        )}
      >
        <a
          href={logo.url}
          className={testId('footer-block__logo-link')}
          target={logo.newWindow ? '_blank' : undefined}
          rel={logo.newWindow ? 'noopener noreferrer' : undefined}
        >
          <span className="jg-sr-only">{logo.label}</span>
          <img
            className={classNames(
              testId('footer-block__logo-img'),
              'jg-chrome-footer__logo-wrapper jg-display-b jg-space-mtxs',
              {
                'jg-chrome-footer__logo-wrapper--jg':
                  logo.version === 'jgDefault' ||
                  logo.version === 'jgGrey' ||
                  logo.version === 'jgNewLogo',
                'jg-chrome-footer__logo-wrapper--bbp2p':
                  logo.version === 'bbP2P',
              },
            )}
            src={logoSrc(logo.version)}
            alt=""
          />
        </a>
        <ul className="jg-space-mbsm jg-space-mbxs@md jg-space-mtms">
          {secondary.map((item, i) => (
            <li
              key={i}
              className="jg-display-b jg-display-ib@md jg-space-mrms@md"
            >
              <a
                className={classNames(
                  testId('footer-block__secondary-link'),
                  'jg-display-b jg-space-pvxs',
                  'jg-text--medium jg-text-no-underline@hover',
                  'jg-color--empress jg-color--nero@hover',
                )}
                href={item.url}
                target={item.newWindow ? '_blank' : undefined}
                rel={item.newWindow ? 'noopener noreferrer' : undefined}
              >
                {item.label}
              </a>
            </li>
          ))}
        </ul>
        {fcaTagline && (
          <div
            className={classNames(
              testId('footer-block__fcaTagline'),
              'jg-chrome-footer__legal-text',
              'jg-space-prmd jg-space-prn@lg jg-space-pbsm',
            )}
          >
            <p
              className={classNames(
                testId('footer-block__fcaTagline-label'),
                'jg-chrome-footer__fcaTagline-text',
                'jg-color--nobel jg-font-weight-regular jg-space-mbn@xl jg-space-pbn',
              )}
            >
              {fcaTagline.label}
            </p>
            <p className={'jg-chrome-footer__fcaTagline-divider'} />
            <p
              className={classNames(
                testId('footer-block__fcaTagline-registration'),
                'jg-chrome-footer__fcaTagline-text',
                'jg-color--nobel jg-font-weight-regular jg-space-mbn',
              )}
            >
              {fcaTagline.registration}
            </p>
          </div>
        )}
        {copyright && (
          <p
            className={classNames(
              testId('footer-block__copyright'),
              'jg-chrome-footer__legal-text',
              'jg-color--nobel jg-font-weight-light jg-space-prmd jg-space-prn@lg',
            )}
          >
            {copyright}
          </p>
        )}
      </div>
      {social &&
        social.length > 0 && [
          <div className="col-xs-12 col-xl-3">
            <div
              className={classNames(
                testId('footer-block__social-label'),
                'jg-text--medium-light jg-space-mbsm jg-space-mtms jg-space-mtxs@xl',
              )}
            >
              {findUs}
            </div>
            <ul className={testId('footer-block__social-links')}>
              {social.map((item, idx) => (
                <li
                  key={idx}
                  className={classNames(
                    'jg-display-ib',
                    'jg-br--circle jg-fill--white',
                    'jg-background--nobel jg-background--empress@hover',
                    'jg-space-mbsm',
                    idx === social.length - 1
                      ? 'jg-space-mrn'
                      : 'jg-space-mrsm',
                  )}
                >
                  <a
                    href={item.url}
                    className={classNames(
                      testId('footer-block__social-link'),
                      'jg-display-ib jg-space-psm',
                    )}
                  >
                    <span className="jg-sr-only">{item.label}</span>
                    <Svg className="jg-icon" markup={socialIcons[item.icon]} />
                  </a>
                </li>
              ))}
            </ul>
          </div>,
        ]}
    </div>
  </div>
);

export default FooterBlock;
